import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, staggerContainer } from "../../utils/animations";
import { Helmet } from "react-helmet";

import HeroSlider from "./HeroSlider";
import ScrollingTags from "./ScrollingTags";
import StatsSection from "./StatsSection";
import OnlyServicesSection from "../Services/OnlyServicesSection";
import OnlyProcessWorkSection from "../OurProcess/OnlyProcessWorkSection";
import WhyUs from "./WhyUs";
import FinalCTA from "./FinalCTA";
import ProblemSection from "./ProblemSection";
import PricingSection from "../Portfolio/PricingSection";

export default function HeroSection() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>
          Ashlar Building Group | High-Value Residential & HOA Construction
        </title>
        <meta
          name="description"
          content="Ashlar Building Group is a third-generation construction firm specializing in high-value residential builds, HOA reconstruction, restoration, and complex project takeovers."
        />
        <meta
          name="keywords"
          content="Construction, Home Remodels, ADUs, Custom Homes, HOA Reconstruction, Restoration, Project Takeover, Third-Generation Contractors"
        />
        <meta property="og:title" content="Ashlar Building Group | Complex Projects Done Right" />
        <meta property="og:description" content="High-value residential construction and HOA reconstruction handled with structure, transparency, and precision." />
        <meta property="og:image" content="/Images/Hero_CustomHome.jpg" />
        <meta property="og:url" content="https://www.ashlarbuildinggroup.com" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative w-full min-h-[85vh] sm:min-h-[80vh] md:min-h-[85vh] flex items-center justify-center overflow-hidden">
        
        {/* 🔥 Auto Slider Background */}
        <HeroSlider />

        {/* Hero Content */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 text-center text-white flex flex-col items-center justify-center h-full"
        >
          <div className="mt-6 sm:mt-10 md:mt-12 w-full">

            {/* Heading */}
            <motion.h1
              variants={fadeUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4"
            >
              Strategic Construction Management
              <span className="block text-[#adb5bd] text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-1">
                Complex Projects. Executed Right.
              </span>
            </motion.h1>

            {/* Paragraph */}
            <motion.p
              variants={fadeUp}
              className="text-sm sm:text-base md:text-lg lg:text-xl text-[#e9ecef] mb-6 max-w-3xl mx-auto"
            >
              Ashlar Building Group is a third-generation firm specializing in high-value residential builds, HOA reconstruction, and complex project takeovers. We execute every project with structure, transparency, and precision.
            </motion.p>

            {/* Buttons */}
            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8"
            >
              <Link to="/contact">
                <button className="border border-[#dee2e6] text-white hover:bg-white/10 px-6 py-3 text-sm sm:text-base md:text-lg rounded transition min-w-[220px] text-center">
                  Request a Project Consultation
                </button>
              </Link>

              <Link to="/services">
                <button className="border border-[#dee2e6] text-white hover:bg-white/10 px-6 py-3 text-sm sm:text-base md:text-lg rounded transition min-w-[220px] text-center">
                  Explore Our Services
                </button>
              </Link>
            </motion.div>

            {/* Stats Section */}
            <motion.div variants={fadeUp} className="w-full">
              <StatsSection />
            </motion.div>

          </div>
        </motion.div>
      </section>

      {/* Other Sections */}
      <ScrollingTags />
      <OnlyServicesSection />
      <OnlyProcessWorkSection />
      <WhyUs />
      <ProblemSection />
      <PricingSection />
      <FinalCTA />
    </>
  );
}